import i18next from 'i18next'
import React, { useEffect, useId, useRef, useState } from 'react'
import { IconFa, TableCellProps } from '../../atoms'
import { PlainButton } from '../../atoms/plain-button'
import { Tooltip } from '../../atoms/tooltip'

export const CopyTableBodyCell = ({
  label,
  tableHeader,
  className,
  copyValue,
  ...props
}: TableCellProps & { copyValue?: string }) => {
  const [tooltipText, setTooltipText] = useState(i18next.t('glow.copyValue', { value: tableHeader }))
  const copyId = useId()
  const valueId = useId()
  const timerIdRef = useRef<NodeJS.Timeout>(undefined)

  useEffect(() => {
    return () => clearTimeout(timerIdRef.current)
  }, [])

  const handleCopy = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    const textToCopy =
      copyValue ??
      (React.isValidElement<{ children?: React.ReactNode }>(label) && label.props.children
        ? String(label.props.children)
        : String(label || ''))

    // Reset tooltip text after copy
    timerIdRef.current = setTimeout(() => {
      setTooltipText(i18next.t('glow.copyValue', { value: tableHeader }))
    }, 2000)

    try {
      await navigator.clipboard.writeText(textToCopy)
      setTooltipText(i18next.t('glow.copied'))
    } catch (err) {
      console.error('Failed to copy text: ', err)
      setTooltipText(i18next.t('glow.copyFailed') || 'Copy failed')
    }
  }

  // Without a copyValue, the copy button is rendered only when the label is either:
  // 1. A valid React element with children, or
  // 2. A non-empty string.
  const showCopyButton =
    copyValue !== undefined
      ? copyValue.trim().length > 0
      : (React.isValidElement<{ children?: React.ReactNode }>(label) &&
          React.Children.count(label.props.children) > 0) ||
        (typeof label === 'string' && label.trim().length > 0)

  return (
    <td {...props} className={className}>
      <div className="flex items-center gap-1">
        <Tooltip referenceId={copyId} label={tooltipText}>
          {showCopyButton && (
            <PlainButton
              type="button"
              id={copyId}
              className="m-1 p-0.5"
              onClick={handleCopy}
              aria-label={i18next.t('glow.copyValue', { value: tableHeader })}
            >
              <IconFa icon={['far', 'copy']} className="text-gray-400" />
            </PlainButton>
          )}
        </Tooltip>
        <Tooltip className="w-full" referenceId={valueId} label={copyValue ?? label}>
          <div className="text-truncate w-[95%]">
            <span id={valueId}>{label}</span>
          </div>
        </Tooltip>
      </div>
    </td>
  )
}
